import api from "../api/client";
const geocodeCache = new Map();
const inflightRequests = new Map();

const dataCache = new Map();
const dataFlights = new Map();
const retryAt = new Map();

export async function reverseGeocodeResponse(latitude, longitude) {
  const key = geocodeKey(latitude, longitude);
  if (!key) return { ok: false, json: async () => null };
  if (dataCache.has(key)) return { ok: true, json: async () => dataCache.get(key) };
  if ((retryAt.get(key) || 0) > Date.now()) return { ok: false, json: async () => null };
  if (!dataFlights.has(key)) {
    dataFlights.set(key, api.get('/geocoding/reverse', { params: { lat: latitude, lon: longitude }, timeout: 5000 })
      .then(response => {
        if (response.status === 200 && response.data?.address) {
          dataCache.set(key, response.data);
          if (dataCache.size > 2000) dataCache.delete(dataCache.keys().next().value);
          return response.data;
        }
        retryAt.set(key, Date.now() + 15000); return null;
      }).catch(() => { retryAt.set(key, Date.now() + 30000); return null; })
      .finally(() => dataFlights.delete(key)));
  }
  const data = await dataFlights.get(key);
  return { ok: Boolean(data), json: async () => data };
}

export const geocodeKey = (latitude, longitude) => {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) {
    return null;
  }
  return `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
};

const formatDecodedAddress = (data) => {
  if (!data) {
    return "";
  }

  const address = data.address || {};
  const primary = address.road || address.highway || address.neighbourhood || address.suburb;
  const secondary =
    address.city || address.town || address.village || address.county || address.state || address.country;

  if (primary && secondary) {
    return `${primary} • ${secondary}`.toUpperCase();
  }

  if (primary) {
    return primary.toUpperCase();
  }

  if (data.display_name) {
    return data.display_name.toUpperCase();
  }

  return "";
};

export const getGeocodeLabel = (key) => {
  if (!key) {
    return undefined;
  }
  return geocodeCache.get(key);
};

export const getCachedGeocodeForCoords = (latitude, longitude) => {
  const key = geocodeKey(latitude, longitude);
  return key ? geocodeCache.get(key) : undefined;
};

export const reverseGeocodeLookup = (latitude, longitude) => {
  const key = geocodeKey(latitude, longitude);
  if (!key) {
    return Promise.resolve("");
  }

  if (geocodeCache.has(key)) {
    return Promise.resolve(geocodeCache.get(key));
  }

  if (inflightRequests.has(key)) {
    return inflightRequests.get(key);
  }

  const request = reverseGeocodeResponse(latitude, longitude)
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      const formatted = formatDecodedAddress(data);
      if (formatted) {
        geocodeCache.set(key, formatted);
        return formatted;
      }
      return "";
    })
    .catch((error) => {
      console.error("Reverse geocode lookup failed", error);
      return "";
    })
    .finally(() => inflightRequests.delete(key));

  inflightRequests.set(key, request);
  return request;
};
